import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogTitle } from "../common/Dialog";
import { fetchSupplyHistory } from "../../apis/api.inventory";
import { queryInventorySupplyHistoryKey } from "../../apis/queryKeys";
import useBrandTranslation from "../../hooks/useBrandTranslation";
import { formatAmount, formatMoney } from "../../utils/inventoryFormat";
import Spinner from "../common/Spinner";
import { formatDateOnly } from "../../utils/inventoryExpiration";

// Historial (evolución de precio y stock) de un insumo.
export default function SupplyHistoryDialog({ supply, ownerId, decimalSeparator, currencySymbol, onClose }) {
  const { t } = useBrandTranslation();
  const open = !!supply;

  const { data, isLoading } = useQuery({
    queryKey: queryInventorySupplyHistoryKey(supply?.id),
    queryFn: () => fetchSupplyHistory(supply.id, ownerId),
    enabled: open,
  });

  const typeLabel = (type) =>
    t(`inventory.history.types.${type}`, type);

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent className="w-[94vw] max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogTitle className="text-lg font-semibold text-gray-900 mb-3">
          {t("inventory.history.title", "Historial de {{name}}", { name: supply?.description || "" })}
        </DialogTitle>

        {isLoading ? (
          <Spinner className="w-8 h-8 mx-auto my-6" />
        ) : !data || data.length === 0 ? (
          <p className="text-gray-500 text-sm py-6 text-center">
            {t("inventory.history.empty", "Sin movimientos registrados")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-3">{t("inventory.history.date", "Fecha")}</th>
                  <th className="py-2 pr-3">{t("inventory.history.change", "Cambio")}</th>
                  <th className="py-2 pr-3">{t("inventory.history.stock", "Stock")}</th>
                  <th className="py-2 pr-3">{t("inventory.history.unitCost", "Costo unitario")}</th>
                  <th className="py-2 pr-3">{t("inventory.history.expiration", "Vencimiento")}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr key={row.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {new Date(row.changed_at).toLocaleString()}
                    </td>
                    <td className="py-2 pr-3">{typeLabel(row.change_type)}</td>
                    <td className="py-2 pr-3">{formatAmount(row.stock, decimalSeparator)}</td>
                    <td className="py-2 pr-3">
                      {formatMoney(row.unit_cost, currencySymbol, decimalSeparator)}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {row.expiration_date ? formatDateOnly(row.expiration_date) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
